import { appendCsrfParams } from '~/common/helpers/csrf'
import { getBaseCartPath, hideLoading } from '~/site/pages/cart/common'
import { processFormSubmission } from './common'

/**
 * Initializes the NMI payment form by setting up required fields and loading the NMI tokenization script.
 * param {string} scopeSelector - The CSS selector for the scope in which the NMI fields are present.
 * param {string} processorId   - The payment processor ID for NMI, with an optional 'pp-' prefix.
 */
export function initializeNmi(scopeSelector, processorId) {
  // Switching away from NMI and back to it reinitializes the fields, so we want to clear the errors too.
  clearErrorElement(scopeSelector)

  let paymentProcessorId = processorId.replace(/pp-/, '')

  let data = new URLSearchParams()
  data.append('payment_method[payment_processor_id]', paymentProcessorId)
  appendCsrfParams(data)

  return fetch(getBaseCartPath() + '/nmi_setup', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: data,
  })
    .then((response) => response.json())
    .then((json) => loadCollectJs(scopeSelector, json.public_security_key))
    .then(() => removeCollectJsFields(document.querySelector(scopeSelector)))
}

const collectJsLoads = new Map()

function loadCollectJs(scopeSelector, key) {
  const id = `${scopeSelector} ${key}`
  if (collectJsLoads.has(id)) return collectJsLoads.get(id)
  const script = document.createElement('script')
  script.src = 'https://secure.nmi.com/token/Collect.js'
  script.setAttribute('data-tokenization-key', key)
  configureFieldPlaceholers(script, scopeSelector)
  const load = new Promise((resolve) => (script.onload = resolve))
  collectJsLoads.set(id, load)
  document.querySelector(`${scopeSelector} .nmi_fields`).append(script)
  return load
}

function removeCollectJsFields(scope) {
  scope.querySelectorAll('.CollectJSInlineIframe').forEach((e) => e.remove())
}

// Using style-sniffer we can define selectors to style
// NMI field placeholders that will be created and placed
// before NMI creates and injects the actual fields (that usually requires some seconds)
// This way the UI is smoother
// https://support.nmi.com/hc/en-gb/articles/360009713318-Collect-js-Tokenization-With-Direct-Connect
function configureFieldPlaceholers(script, selector) {
  script.setAttribute('data-style-sniffer', true)
  if (selector == '.nmi_credit_card') {
    script.setAttribute('data-field-ccnumber-selector', '#nmi-ccnumber')
    script.setAttribute('data-field-ccexp-selector', '#nmi-ccexp')
    script.setAttribute('data-field-cvv-selector', '#nmi-cvv')
  } else if (selector == '.nmi_ach') {
    script.setAttribute('data-field-checkname-selector', '#nmi-checkname')
    script.setAttribute('data-field-checkaccount-selector', '#nmi-checkaccount')
    script.setAttribute('data-field-checkaba-selector', '#nmi-checkaba')
  }
}

export function handleValidationFail(scopeSelector, field, status, message) {
  if (
    !document.querySelector(scopeSelector).querySelector(`[id$='${field}']`)
  ) {
    // If you put in an invalid value then switch between NMI Credit Card and NMI ACH, it fires the validation on both.
    // Don't put a validation message for something that's not in the current scope.
    console.log(
      `Received validation for field ${field} but that's not present within ${scopeSelector}.`
    )
    return
  }

  let errorElement = getErrorElement(scopeSelector)
  let fieldError = errorElement.querySelector('.error-message--' + field)
  if (status) {
    if (fieldError) {
      fieldError.remove()
    }
    if (!errorElement.querySelector('.error-message:not(:empty)')) {
      errorElement.style.display = 'none'
      errorElement.classList.add('hidden') // hidden have !important, 'block' does not work in some cases
    }
    return
  }

  if (!fieldError) {
    fieldError = document.createElement('div')
    fieldError.setAttribute('class', 'error-message error-message--' + field)
    errorElement.appendChild(fieldError)
  }

  if (message == 'Field is empty') {
    switch (field) {
      case 'ccnumber':
        message = 'Credit card number is required'
        break
      case 'ccexp':
        message = 'Credit card expiry is required'
        break
      case 'cvv':
        message = 'Credit card CVV is required'
        break
      case 'checkname':
        message = 'Account holder name is required'
        break
      case 'checkaccount':
        message = 'Account number is required'
        break
      case 'checkaba':
        message = 'Routing number is required'
        break
    }
  }
  fieldError.innerText = message

  showErrorElement(scopeSelector)
  hideLoading()
}

function getErrorElement(scopeSelector) {
  return document.querySelector(scopeSelector).querySelector('.nmi_errors')
}

function showErrorElement(scopeSelector) {
  let errorElement = getErrorElement(scopeSelector)
  errorElement.style.display = 'block'
  errorElement.classList.remove('hidden') // hidden have !important, 'block' does not work in some cases
}

function clearErrorElement(scopeSelector) {
  let error = getErrorElement(scopeSelector)
  error.style.display = 'none'
  error.innerHTML = ''
}

export async function submitWithSavedNmi(event) {
  event.preventDefault()

  let form = event.target

  hideLoaderIfContract()

  processFormSubmission(form, {
    onSuccess: (json) => {
      location.href = json.callback_url
    },
  })
}

export async function submitWithNewNmi(scopeSelector, response, data) {
  let form = document.querySelector('.cart-checkout-payment')

  hideLoaderIfContract()
  getErrorElement(scopeSelector).style.display = 'none'

  processFormSubmission(form, {
    getFormData: () => {
      let formData = new FormData(form)
      for (let key in data) {
        formData.append(key, data[key])
      }
      return formData
    },
    onSuccess: (json) => {
      location.href = json.callback_url
    },
    onError: (json) => {
      // The captcha prompt is in the cart errors container; it is not a gateway decline.
      if (json?.captcha_required) return

      getErrorElement(scopeSelector).innerText =
        'The payment gateway rejected the transaction: ' + json.error
      showErrorElement(scopeSelector)
    },
  })
}

// Collect.js reads colors only at configure time and paints the iframe document
// itself. The field group carries the surface (bg-bg-alt). Its own computed
// color does not flip in dark mode and matches that surface, which would hide
// typed digits. Plain inputs use text-fg-base, so resolve that token here.
export function collectInlineFieldCss(scopeSelector) {
  const group = document.querySelector(
    `${scopeSelector} .nmi_fields .t-field-group`
  )
  if (!group) return {}
  const style = getComputedStyle(group)
  const css = { 'background-color': style.backgroundColor }
  const text = foregroundColor(group, style.backgroundColor)
  if (text) css.color = text
  return css
}

function foregroundColor(group, background) {
  const probe = document.createElement('span')
  probe.style.color = 'var(--tw-color-fg-base)'
  group.appendChild(probe)
  const color = getComputedStyle(probe).color
  probe.remove()
  if (!color || color === background) return null
  return color
}

export function chooseNonNmi(scopeSelector, reconfigureCollectJS) {
  let nmiFields = document.querySelector(scopeSelector)
  if (nmiFields) {
    nmiFields.style.display = 'none'
    if (reconfigureCollectJS && window.CollectJS) {
      // Call configure again so we unbind from the submit button.
      window.CollectJS.configure({
        paymentSelector: '.disable-nmi-callbacks',
      })
    }
    removeCollectJsFields(nmiFields)
  }
}

function hideLoaderIfContract() {
  const contractsController = document.querySelector(
    '[data-controller="contracts--contracts"]'
  )
  if (contractsController) {
    hideLoading()
  }
}
